import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { environment } from "../../environments/environment";
import { Observable } from "rxjs";
import { map, shareReplay } from "rxjs/operators";

export enum Department {
  ACEL = "Department of Agricultural Communication, Education, and Leadership",
  AEDE = "Department of Agricultural, Environmental, and Development Economics",
  ANSCI = "Department of Animal Sciences",
  ENT = "Department of Entomology",
  EXT = "Department of Extension",
  FST = "Department of Food Science and Technology",
  FABE = "Department of Food, Agricultural and Biological Engineering",
  HCS = "Department of Horticulture and Crop Science",
  SENR = "School of Environment and Natural Resources",
  ATI = "Ohio State Agricultural Technical Institute",
  PlantPath = "Department of Plant Pathology",
}

export enum DepartmentURL {
  ACEL = "https://acel.osu.edu/",
  AEDE = "https://aede.osu.edu/",
  ANSCI = "https://ansci.osu.edu/",
  ENT = "https://entomology.osu.edu/home",
  EXT = "https://extension.osu.edu/",
  FST = "https://fst.osu.edu/home",
  FABE = "https://fabe.osu.edu/",
  HCS = "https://hcs.osu.edu/",
  SENR = "https://senr.osu.edu/",
  ATI = "https://ati.osu.edu/",
  PlantPath = "https://plantpath.osu.edu/",
}

export type ExpertFields = {
  name?: string;
  name_dot_number?: string;
  legal_first_name?: string;
  legal_last_name?: string;
  department?: keyof typeof Department;
  position_title?: string;
  email?: string;
  keywords?: string[];
  short_keywords?: string[];
  searchable_blob?: string;
  summary?: string;
  new?: string;
};

export type ExpertHit = { id: string; fields: ExpertFields };

/**
 * Frozen expert-directory snapshot on the content API.
 * Replaces the CloudSearch `expert-directory` domain (API Gateway ztaryerdq4).
 * The directory is ~300 people, so search and department filters run in the browser.
 */
const expertsUrl = `${environment.contentApiUrl}/experts?_format=json`;

@Injectable({
  providedIn: "root",
})
export class ExpertSearchService {
  private readonly allExperts$: Observable<ExpertHit[]> = this._http
    .get<ExpertHit[]>(expertsUrl)
    .pipe(shareReplay(1));

  constructor(private _http: HttpClient) {}

  public search({
    query,
    departments,
    filters,
    size,
  }: {
    query?: string;
    departments?: (keyof typeof Department)[];
    filters?: ExpertFields;
    size?: number;
  }): Observable<ExpertHit[]> {
    const limit = size ?? 1000;
    return this.allExperts$.pipe(
      map((experts) =>
        this.filterExperts(experts, query, departments, filters).slice(0, limit)
      )
    );
  }

  public getFieldOptions(fieldName: string) {
    return new Promise<Set<string>>((resolve, reject) => {
      let options: Set<string> = new Set<string>();
      this.search({}).subscribe((result) => {
        result.forEach((res) => {
          let fieldValues = res.fields[fieldName as keyof ExpertFields];
          if (fieldValues) {
            if (fieldName == "authors" && Array.isArray(fieldValues)) {
              fieldValues.forEach((el) => options.add(String(el)));
            } else options.add(String(fieldValues));
          }
        });

        if (options.size > 0) resolve(options);
        else reject(`No fields titled ${fieldName}.`);
      });
    });
  }

  private filterExperts(
    experts: ExpertHit[],
    query: string | undefined,
    departments: (keyof typeof Department)[] | undefined,
    filters: ExpertFields | undefined
  ): ExpertHit[] {
    const tokens = this.queryTokens(query);
    const deptFilter =
      departments && departments.length > 0 ? new Set(departments) : null;

    return experts.filter((expert) => {
      if (deptFilter && !deptFilter.has(expert.fields.department)) return false;
      if (!this.matchesFilters(expert.fields, filters)) return false;
      if (!tokens.length) return true;
      const haystack = this.searchText(expert.fields);
      return tokens.every((token) => haystack.includes(token));
    });
  }

  /** Empty query and the old CloudSearch match-all (`x|-x`) return everyone. */
  private queryTokens(query: string | undefined): string[] {
    const q = (query ?? "").trim();
    if (!q || q === "x|-x" || q === "*") return [];
    return q
      .toLowerCase()
      .split(/\s+/)
      .filter((token) => token.length > 0);
  }

  private searchText(fields: ExpertFields): string {
    const departmentName = fields.department
      ? Department[fields.department] ?? ""
      : "";
    return [
      fields.name,
      fields.legal_first_name,
      fields.legal_last_name,
      fields.name_dot_number,
      fields.email,
      fields.department,
      departmentName,
      fields.position_title,
      fields.summary,
      ...(fields.keywords ?? []),
      ...(fields.short_keywords ?? []),
    ]
      .filter((part) => part != null && part !== "")
      .join(" ")
      .toLowerCase();
  }

  private matchesFilters(
    fields: ExpertFields,
    filters: ExpertFields | undefined
  ): boolean {
    if (!filters) return true;
    for (const filterName of Object.keys(filters) as (keyof ExpertFields)[]) {
      const wanted = filters[filterName];
      if (wanted == null) continue;
      if (typeof wanted === "string" && wanted.length <= 1) continue;
      const actual = fields[filterName];
      if (Array.isArray(actual)) {
        if (!actual.map(String).includes(String(wanted))) return false;
      } else if (String(actual ?? "") !== String(wanted)) {
        return false;
      }
    }
    return true;
  }
}
